/**
 * The count on the tab, in three places at once.
 *
 * An agent works with this app in a background tab most of the day, and the tab
 * strip is the only part of it they can see from another window. So the count goes
 * everywhere the OS will draw it:
 *
 * - <b>The title</b>, as `(3) WhatsApp Platform`. The oldest and most reliable of
 *   the three: it works in every browser and it is what shows in a window list, an
 *   alt-tab switcher and a pinned-tab tooltip.
 * - <b>The favicon</b>, drawn here. The app had no favicon at all, so there was
 *   nothing to badge — the tab showed the browser's default globe, which is how an
 *   agent with nine tabs open loses this one. The mark is drawn rather than shipped
 *   as a file, for the same reason the chime is synthesised: no asset to go missing
 *   behind a static-file route, and one place that knows what the mark looks like.
 * - <b>The app badge</b>, via `navigator.setAppBadge`, which is the dot on the dock
 *   or taskbar icon when the app has been installed. Absent in most browsers, so it
 *   is called defensively and its absence is not a failure.
 */

/** The title as the document was served with, before anything was prefixed to it. */
const baseTitle = typeof document === 'undefined' ? '' : document.title;

let drawn: number | null = null;

/**
 * Sets the count everywhere. `0` clears it.
 *
 * Guarded on the last value it drew: this is called from a React effect that runs
 * whenever the count query settles, which is several times a minute, and
 * regenerating a canvas and replacing a DOM node each time for the same number
 * would be work nobody asked for.
 */
export function setTabBadge(count: number): void {
  const next = Math.max(0, Math.floor(count));
  if (drawn === next) return;
  drawn = next;

  document.title = next > 0 ? `(${next > 99 ? '99+' : next}) ${baseTitle}` : baseTitle;

  applyFavicon(next);

  // Installed-app badge. Present on very few browsers; `void` and a catch because
  // it rejects rather than throwing where permission is absent.
  const nav = navigator as Navigator & {
    setAppBadge?: (n?: number) => Promise<void>;
    clearAppBadge?: () => Promise<void>;
  };

  try {
    if (next > 0) void nav.setAppBadge?.(next).catch(() => { /* not available */ });
    else void nav.clearAppBadge?.().catch(() => { /* not available */ });
  } catch {
    // Older Safari throws synchronously rather than rejecting.
  }
}

/** Restores the plain title and mark. Called when the app unmounts or signs out. */
export function clearTabBadge(): void {
  setTabBadge(0);
}

function applyFavicon(count: number): void {
  const url = drawIcon(count);
  if (!url) return;

  let link = document.querySelector<HTMLLinkElement>('link[rel="icon"]');

  if (!link) {
    link = document.createElement('link');
    link.rel = 'icon';
    document.head.appendChild(link);
  }

  link.type = 'image/png';
  link.href = url;
}

/**
 * The app mark, with the count over it.
 *
 * 64px rather than 32: browsers downscale a favicon to whatever the platform asks
 * for, and a two-digit number rendered at 32 and then downscaled again is a smudge.
 * Drawing at 64 and letting the browser scale down keeps the digits legible at the
 * 16px the tab strip actually uses.
 *
 * Returns null where there is no canvas — a very old browser, or a test environment.
 * The title still carries the count, so the feature degrades rather than breaking.
 */
function drawIcon(count: number): string | null {
  const size = 64;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;

  const ctx = canvas.getContext('2d');
  if (!ctx) return null;

  // The same speech bubble the header's Brand draws, at 64px. Read from the
  // stylesheet rather than hard-coded, so the mark follows the brand colour if it
  // ever changes — with a literal fallback, because getComputedStyle on the root
  // returns an empty string before the stylesheet has parsed.
  const brand = getComputedStyle(document.documentElement)
    .getPropertyValue('--color-brand').trim() || '#128c7e';

  ctx.fillStyle = brand;
  roundedRect(ctx, 2, 2, size - 4, size - 4, 14);
  ctx.fill();

  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = 6;
  ctx.lineCap = 'round';
  ctx.lineJoin = 'round';
  ctx.beginPath();
  ctx.moveTo(19, 32);
  ctx.lineTo(29, 42);
  ctx.lineTo(46, 23);
  ctx.stroke();

  if (count > 0) {
    const label = count > 99 ? '99+' : String(count);

    // Bottom-right, and wide enough for the text rather than a fixed circle: "99+"
    // in a circle sized for "3" is three characters overflowing a red dot.
        ctx.font = 'bold 30px system-ui, -apple-system, sans-serif';
    const width = Math.max(30, ctx.measureText(label).width + 14);
    const height = 32;
    const x = size - width - 1;
    const y = size - height - 1;

    // A ring in the mark's own colour between the pill and the mark, so the count
    // reads as sitting on top rather than merging into the green behind it.
    ctx.fillStyle = brand;
    roundedRect(ctx, x - 3, y - 3, width + 6, height + 6, (height + 6) / 2);
    ctx.fill();

    ctx.fillStyle = '#e5484d';
    roundedRect(ctx, x, y, width, height, height / 2);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(label, x + width / 2, y + height / 2 + 1);
  }

  return canvas.toDataURL('image/png');
}

function roundedRect(
  ctx: CanvasRenderingContext2D,
  x: number, y: number, width: number, height: number, radius: number,
): void {
  const r = Math.min(radius, width / 2, height / 2);
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.arcTo(x + width, y, x + width, y + height, r);
  ctx.arcTo(x + width, y + height, x, y + height, r);
  ctx.arcTo(x, y + height, x, y, r);
  ctx.arcTo(x, y, x + width, y, r);
  ctx.closePath();
}
